import axios from 'axios';
import { type ChangeEvent, type FormEvent, useCallback, useMemo, useState } from 'react';

import { getServiceId } from '@/api/tokens.helper.ts';
import { Checkbox, DateTimePicker, Modal } from '@/components';
import { ButtonOutlinedSmall } from '@/components/Button';
import { Input, Radio } from '@/components/Inputs';
import { useAuth } from '@/store/auth/auth.store';
import { useBackgroundTasksStore } from '@/store/background-tasks/background-tasks.store.ts';
import type {
  ItineraryActivities,
  ItineraryData,
  ItineraryDiningStyle,
  ItineraryPace,
  ItineraryPrefill,
  ItinerarySuggestionResponse,
  ItineraryType,
  ItineraryVibes,
} from '@/types/itineraryTypes.ts';
import { extractAI, parseAIError } from '@/utils/extractAI.ts';
import { toast } from '@/utils/toast.ts';
import RadialProgress from '@components/RadialProgress/RadialProgress.tsx';

import styles from './ItinerarySuggestionsModal.module.scss';

type ItinerarySuggestionsProps = {
  title: string;
  open: boolean;
  onClose: () => void;
  questions: ItineraryType[] | null;
  prefill?: ItineraryPrefill | null;
};

const PACE_OPTIONS = [
  { label: 'relaxed', id: 'relaxed' },
  { label: 'balanced', id: 'balanced' },
  { label: 'active', id: 'active' },
] satisfies { label: ItineraryPace; id: ItineraryPace }[];

const VIBES_OPTIONS = [
  { label: 'relaxation_wellness', id: 'relaxation_wellness' },
  { label: 'adventure_watersports', id: 'adventure_watersports' },
  { label: 'nature_seclusion', id: 'nature_seclusion' },
  { label: 'culture_sightseeing', id: 'culture_sightseeing' },
  { label: 'food_wine', id: 'food_wine' },
  { label: 'nightlife_social', id: 'nightlife_social' },
  { label: 'family_friendly', id: 'family_friendly' },
  { label: 'romantic', id: 'romantic' },
] satisfies { label: ItineraryVibes; id: ItineraryVibes }[];

const ACTIVITIES_OPTIONS = [
  { label: 'diving_snorkeling', id: 'diving_snorkeling' },
  { label: 'beach', id: 'beach' },
  { label: 'watersports_toys', id: 'watersports_toys' },
  { label: 'fishing', id: 'fishing' },
  { label: 'hiking', id: 'hiking' },
  { label: 'shopping', id: 'shopping' },
  { label: 'sightseeing', id: 'sightseeing' },
  { label: 'spa', id: 'spa' },
  { label: 'dining_ashore', id: 'dining_ashore' },
] satisfies { label: ItineraryActivities; id: ItineraryActivities }[];

const DINING_STYLE_OPTIONS = [
  { label: 'chef_onboard', id: 'chef_onboard' },
  { label: 'ashore', id: 'ashore' },
  { label: 'mix', id: 'mix' },
] satisfies { label: ItineraryDiningStyle; id: ItineraryDiningStyle }[];

const formatOptionLabel = (value: string) =>
  value.replace(/_/g, ' ').replace(/^./, (letter) => letter.toUpperCase());

const requiredFieldError = (field: string) => `Please fill in the ${field}.`;
const POLLING_INTERVAL = 30000;
const MAX_POLLING_ATTEMPTS = 200;

type ItineraryJobResponse = {
  job_id: string;
};

const initialWizardData: ItineraryData['wizard'] = {
  departure: '',
  start_date: '',
  end_date: '',
  destination: '',
  pace: 'balanced',
  vibes: [],
  party: '',
  activities: [],
  dining: {
    style: 'mix',
    cuisines: [],
    allergies: [],
  },
  must_include_avoid: '',
  special_occasion: '',
};

const applyPrefill = (prefill?: ItineraryPrefill | null): ItineraryData['wizard'] => {
  if (!prefill) return initialWizardData;

  return {
    ...initialWizardData,
    pace: prefill.pace ?? initialWizardData.pace,
    vibes: prefill.vibes ?? initialWizardData.vibes,
    activities: prefill.activities ?? initialWizardData.activities,
    dining: {
      style: prefill.dining?.style ?? initialWizardData.dining.style,
      cuisines: prefill.dining?.cuisines ?? initialWizardData.dining.cuisines,
      allergies: prefill.dining?.allergies ?? initialWizardData.dining.allergies,
    },
  };
};

const ItinerarySuggestionsModal = ({
  title,
  open,
  onClose,
  questions,
  prefill,
}: ItinerarySuggestionsProps) => {
  const vesselId = getServiceId();
  const userId = useAuth((state) => state.session?.id);
  const questionsKey = questions?.map((question) => question.id).join('|') ?? '';
  const [stepState, setStepState] = useState({ questionsKey, step: 0 });
  const [wizardData, setWizardData] = useState<ItineraryData['wizard']>(() =>
    applyPrefill(prefill),
  );
  const [isValidationShown, setIsValidationShown] = useState(false);

  const step = stepState.questionsKey === questionsKey ? stepState.step : 0;
  const lastStep = Math.max((questions?.length ?? 0) - 1, 0);
  const currentQuestion = questions?.[step];
  const isDateRangeInvalid =
    !!wizardData.start_date &&
    !!wizardData.end_date &&
    new Date(wizardData.start_date) > new Date(wizardData.end_date);

  const prevStepHandler = () => {
    setStepState((state) => ({
      questionsKey,
      step: state.questionsKey === questionsKey && state.step > 0 ? state.step - 1 : 0,
    }));
  };
  const nextStepHandler = (event: { preventDefault: () => void }) => {
    event.preventDefault();
    setStepState((state) => ({
      questionsKey,
      step: state.questionsKey === questionsKey ? Math.min(state.step + 1, lastStep) : 0,
    }));
  };
  const closeHandler = () => {
    setStepState({ questionsKey, step: 0 });
    setWizardData(applyPrefill(prefill));
    setIsValidationShown(false);
    onClose();
  };

  const handleTextChange = useCallback(
    (field: keyof ItineraryData['wizard']) => (event: ChangeEvent<HTMLInputElement>) => {
      setWizardData((data) => ({ ...data, [field]: event.target.value }));
    },
    [],
  );

  const handleRangeChange = useCallback((start: string | null, end: string | null) => {
    setWizardData((data) => ({ ...data, start_date: start ?? '', end_date: end ?? '' }));
  }, []);

  const checkboxList = <T extends ItineraryActivities | ItineraryVibes>(
    name: string,
    options: { label: T; id: T }[],
    values: T[],
    onChange: (values: T[]) => void,
  ) => (
    <div className={styles.checkboxList}>
      {options.map(({ id, label }) => (
        <Checkbox
          key={id}
          id={`${name}-${id}`}
          label={formatOptionLabel(label)}
          checked={values.includes(id)}
          onChange={(event) => {
            const nextValues = event.target.checked
              ? [...values, id]
              : values.filter((value) => value !== id);
            onChange(nextValues);
          }}
        />
      ))}
    </div>
  );

  const validationErrors = useMemo(
    () => ({
      departure: !wizardData.departure.trim(),
      start_date: !wizardData.start_date,
      end_date: !wizardData.end_date || isDateRangeInvalid,
      destination: !wizardData.destination?.trim(),
      pace: !wizardData.pace,
      vibes: wizardData.vibes.length === 0,
      party: !wizardData.party?.trim(),
      activities: wizardData.activities.length === 0,
      dining_style: !wizardData.dining.style,
      cuisines: wizardData.dining.cuisines.length === 0,
      allergies: wizardData.dining.allergies.length === 0,
      must_include_avoid: !wizardData.must_include_avoid?.trim(),
      special_occasion: !wizardData.special_occasion?.trim(),
    }),
    [isDateRangeInvalid, wizardData],
  );

  const hasValidationErrors = Object.values(validationErrors).some(Boolean);

  const content = useMemo(
    () => ({
      departure: (
        <Input
          label="Departure"
          mandatory
          value={wizardData.departure}
          onChange={handleTextChange('departure')}
          error={
            isValidationShown && validationErrors.departure
              ? requiredFieldError('departure')
              : undefined
          }
        />
      ),
      dates: (
        <DateTimePicker
          label="Dates"
          placeholder="Choose dates"
          mandatory
          endRequired
          value={wizardData.start_date || null}
          endValue={wizardData.end_date || null}
          onRangeChange={handleRangeChange}
          minDate={new Date()}
          error={
            isValidationShown && (validationErrors.end_date || validationErrors.start_date)
              ? isDateRangeInvalid
                ? 'The end date is before the start date.'
                : requiredFieldError('dates')
              : undefined
          }
        />
      ),
      destination: (
        <Input
          label="Destination"
          mandatory
          value={wizardData.destination}
          onChange={handleTextChange('destination')}
          error={
            isValidationShown && validationErrors.destination
              ? requiredFieldError('destination')
              : undefined
          }
        />
      ),
      pace: (
        <div className={styles.paceList}>
          {PACE_OPTIONS.map(({ id, label }) => (
            <Radio
              key={id}
              label={formatOptionLabel(label)}
              checked={wizardData.pace === id}
              onChange={() => setWizardData((data) => ({ ...data, pace: id }))}
            />
          ))}
          {isValidationShown && validationErrors.pace && (
            <span className={styles.groupError}>{requiredFieldError('pace')}</span>
          )}
        </div>
      ),
      vibes: (
        <div>
          <p className={styles.fieldLabel}>Vibes</p>
          {checkboxList('vibes', VIBES_OPTIONS, wizardData.vibes, (vibes) =>
            setWizardData((data) => ({ ...data, vibes })),
          )}
          {isValidationShown && validationErrors.vibes && (
            <span className={styles.groupError}>{requiredFieldError('vibes')}</span>
          )}
        </div>
      ),
      party: (
        <Input
          label="Party"
          mandatory
          value={wizardData.party}
          onChange={handleTextChange('party')}
          error={
            isValidationShown && validationErrors.party ? requiredFieldError('party') : undefined
          }
        />
      ),
      activities: (
        <div>
          <p className={styles.fieldLabel}>Activities</p>
          {checkboxList('activities', ACTIVITIES_OPTIONS, wizardData.activities, (activities) =>
            setWizardData((data) => ({ ...data, activities })),
          )}
          {isValidationShown && validationErrors.activities && (
            <span className={styles.groupError}>{requiredFieldError('activities')}</span>
          )}
        </div>
      ),
      dining: (
        <div className={styles.diningFields}>
          <div>
            <p className={styles.fieldLabel}>Dining Style</p>
            <div className={styles.paceList}>
              {DINING_STYLE_OPTIONS.map(({ id, label }) => (
                <Radio
                  key={id}
                  label={formatOptionLabel(label)}
                  checked={wizardData.dining.style === id}
                  onChange={() =>
                    setWizardData((data) => ({
                      ...data,
                      dining: { ...data.dining, style: id },
                    }))
                  }
                />
              ))}
            </div>
            {isValidationShown && validationErrors.dining_style && (
              <span className={styles.groupError}>{requiredFieldError('dining style')}</span>
            )}
          </div>
          <Input
            label="Cuisines"
            value={wizardData.dining.cuisines.join(', ')}
            error={
              isValidationShown && validationErrors.cuisines
                ? requiredFieldError('cuisines')
                : undefined
            }
            onChange={(event) =>
              setWizardData((data) => ({
                ...data,
                dining: {
                  ...data.dining,
                  cuisines: event.target.value
                    .split(',')
                    .map((item) => item.trim())
                    .filter(Boolean),
                },
              }))
            }
          />
          <Input
            label="Allergies"
            value={wizardData.dining.allergies.join(', ')}
            error={
              isValidationShown && validationErrors.allergies
                ? requiredFieldError('allergies')
                : undefined
            }
            onChange={(event) =>
              setWizardData((data) => ({
                ...data,
                dining: {
                  ...data.dining,
                  allergies: event.target.value
                    .split(',')
                    .map((item) => item.trim())
                    .filter(Boolean),
                },
              }))
            }
          />
        </div>
      ),
      must_include_avoid: (
        <Input
          label="Must Include / Avoid"
          mandatory
          value={wizardData.must_include_avoid}
          onChange={handleTextChange('must_include_avoid')}
          error={
            isValidationShown && validationErrors.must_include_avoid
              ? requiredFieldError('must include / avoid')
              : undefined
          }
        />
      ),
      special_occasion: (
        <Input
          label="Special Occasion"
          mandatory
          value={wizardData.special_occasion}
          onChange={handleTextChange('special_occasion')}
          error={
            isValidationShown && validationErrors.special_occasion
              ? requiredFieldError('special occasion')
              : undefined
          }
        />
      ),
    }),
    [
      handleRangeChange,
      handleTextChange,
      isDateRangeInvalid,
      isValidationShown,
      validationErrors,
      wizardData,
    ],
  );

  const sendItineraryHandler = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsValidationShown(true);

    if (hasValidationErrors) {
      toast.error(requiredFieldError('required fields'));
      return;
    }

    const itineraryData: ItineraryData = {
      vessel_id: vesselId ?? '',
      user_id: userId,
      wizard: wizardData,
    };

    /* Announced and dismissed when the task is handed off, not from inside it: the
       point of the message is that the wait is long and nothing needs watching,
       which is information the reader needs at the start of it. */
    toast.info('You’ll get a notification when the itinerary’s ready.');
    onClose();

    void useBackgroundTasksStore.getState().runTask({
      type: 'itinerary',
      label: 'Itinerary Suggestions',
      navigateTo: '/events',
      fn: async (signal) => {
        const result = await extractAI({
          formData: itineraryData,
          type: 'itinerary',
          method: 'suggest',
          isJson: true,
          signal,
        });

        if (result?.status !== 202 || !(result.data as ItineraryJobResponse).job_id) {
          throw new Error('Failed to start itinerary generation');
        }

        const { job_id } = result.data as ItineraryJobResponse;

        for (let attempt = 0; attempt < MAX_POLLING_ATTEMPTS; attempt += 1) {
          if (signal.aborted) {
            throw new DOMException('Aborted', 'AbortError');
          }

          let response;
          try {
            response = await axios.get<ItinerarySuggestionResponse>(
              `${import.meta.env.VITE_APP_EXTRACT_URL}/api/ai/itinerary/suggest/status/${job_id}`,
              { signal },
            );
          } catch (error) {
            if (signal.aborted) {
              throw new DOMException('Aborted', 'AbortError');
            }
            throw new Error(parseAIError(error).message);
          }

          if (response.data.status === 'done') {
            return response.data;
          }

          if (response.data.status === 'error' || response.data.status === 'failed') {
            throw new Error(response.data.error || 'Itinerary generation failed');
          }

          await new Promise((resolve) => setTimeout(resolve, POLLING_INTERVAL));
        }

        throw new Error('Itinerary generation timed out');
      },
    });
  };

  return (
    <Modal title={title} onClose={closeHandler} open={open}>
      <RadialProgress className={styles.progress} count={step} maxValue={lastStep} minValue={0} />
      <form className={styles.modalContent} onSubmit={sendItineraryHandler}>
        {currentQuestion && (
          <div className={styles.questionWrapper}>
            <div className={styles.questionContent}>
              <p>{currentQuestion.label}</p>
              {content[currentQuestion.id]}
            </div>
          </div>
        )}
        <div className={styles.actions}>
          <ButtonOutlinedSmall type="button" disabled={step === 0} onClick={prevStepHandler}>
            Back
          </ButtonOutlinedSmall>
          {questions && lastStep === step ? (
            <ButtonOutlinedSmall disabled={lastStep !== step} type="submit">
              Finish
            </ButtonOutlinedSmall>
          ) : (
            <ButtonOutlinedSmall type={'button'} onClick={nextStepHandler}>
              Next
            </ButtonOutlinedSmall>
          )}
        </div>
      </form>
    </Modal>
  );
};

export default ItinerarySuggestionsModal;
